import usePlaylistAPIClient from "./usePlaylistAPIClient";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
    Assignment,
    DraftAssignment,
    DraftPlaylist,
    EditableAssignment,
    PlaylistItem,
    Playlist,
    MinimalPlaylistItem,
} from "../type";
import queryKeys from "./queryKeys";
import { useUserId } from "../../../../hooks/useUserInfo";
import { ResourcePath } from "../Teacher/utils";

// TODO : Error handling

export const useUpdateAssignmentMutation = () => {
    const teacher = useUserId();
    const { updateAssignment } = usePlaylistAPIClient();
    const queryClient = useQueryClient();

    return useMutation(
        (assignment: EditableAssignment) => updateAssignment(assignment),
        {
            onSuccess: (data: Assignment) => {
                if (!data || !data.assigned_resource?.resource_id) return;
                queryClient.setQueryData<Assignment[]>(
                    queryKeys.getAssignments({ teacher }),
                    (prevData) => {
                        if (!prevData) return undefined;
                        return prevData.map((assignment) =>
                            assignment.assigned_resource &&
                            assignment.assigned_resource.resource_id ===
                                data.assigned_resource!.resource_id
                                ? data
                                : assignment
                        );
                    }
                );
                queryClient.setQueryData<Assignment[]>(
                    queryKeys.getAssignments({
                        resource: data.assigned_resource.resource_id,
                    }),
                    () => [data]
                );
            },
            onError: () => {
                alert("there was an error");
            },
        }
    );
};

export const useCreateAssignmentMutation = () => {
    const teacher = useUserId();
    const { postAssignment } = usePlaylistAPIClient();
    const queryClient = useQueryClient();

    return useMutation(
        (assignment: DraftAssignment) => postAssignment(assignment),
        {
            onSuccess: (data: Assignment) => {
                if (!data || !data.assigned_resource?.resource_id) return;
                queryClient.setQueryData<Assignment[]>(
                    queryKeys.getAssignments({ teacher }),
                    (prevData) => {
                        if (!prevData) return undefined;
                        return prevData.map((assignment) =>
                            assignment.assigned_resource &&
                            assignment.assigned_resource.resource_id ===
                                data.assigned_resource!.resource_id
                                ? data
                                : assignment
                        );
                    }
                );
                queryClient.setQueryData<Assignment[]>(
                    queryKeys.getAssignments({
                        resource: data.assigned_resource.resource_id,
                    }),
                    () => [data]
                );
            },
            onError: () => {
                alert("there was an error");
            },
        }
    );
};

export const useUpdatePlaylistMutation = () => {
    const userId = useUserId();
    const { putPlaylist } = usePlaylistAPIClient();
    const queryClient = useQueryClient();

    return useMutation((playlist: DraftPlaylist) => putPlaylist(playlist), {
        onSuccess: (data: Playlist<PlaylistItem>) => {
            if (!data) return;
            queryClient.setQueryData<Playlist<PlaylistItem>[]>(
                queryKeys.getPlaylists({ author_id: userId }),
                (prevData) => {
                    if (!prevData) return prevData;
                    return prevData.map((playlist) =>
                        playlist.learning_set_id === data.learning_set_id
                            ? data
                            : playlist
                    );
                }
            );
            queryClient.setQueryData<Playlist<PlaylistItem>>(
                queryKeys.getPlaylist(data.learning_set_id, {
                    author_id: userId,
                }),
                () => data
            );
        },
        onError: () => {
            alert("there was an error");
        },
    });
};

export const useUpdateStatusMutation = () => {
    const userId = useUserId();
    const { putPlaylistStatus } = usePlaylistAPIClient();
    const queryClient = useQueryClient();

    return useMutation(
        (
            playlist: Pick<Playlist, "learning_set_id" | "status" | "author_id">
        ) => putPlaylistStatus(playlist),
        {
            onSuccess: (data: Playlist<PlaylistItem>) => {
                if (!data) return;
                queryClient.setQueryData<Playlist<PlaylistItem>[]>(
                    queryKeys.getPlaylists({ author_id: userId }),
                    (prevData) => {
                        if (!prevData) return prevData;
                        return prevData.map((playlist) =>
                            playlist.learning_set_id === data.learning_set_id
                                ? data
                                : playlist
                        );
                    }
                );
                queryClient.setQueryData<Playlist<PlaylistItem>>(
                    queryKeys.getPlaylist(data.learning_set_id, {
                        author_id: userId,
                    }),
                    () => data
                );
            },
            onError: () => {
                alert("there was an error");
            },
        }
    );
};

export const useCreatePlaylistMutation = () => {
    const userId = useUserId();
    const { postPlaylist } = usePlaylistAPIClient();
    const queryClient = useQueryClient();

    return useMutation(
        (
            playlist: Omit<
                DraftPlaylist<ResourcePath | MinimalPlaylistItem>,
                "dirty"
            >
        ) => postPlaylist(playlist),
        {
            onSuccess: (data: Playlist<PlaylistItem>) => {
                if (!data) return;
                queryClient.setQueryData<Playlist<PlaylistItem>[]>(
                    queryKeys.getPlaylists({ author_id: userId }),
                    (prevData) => {
                        if (!prevData) return prevData;
                        return [...prevData, data];
                    }
                );
                queryClient.setQueryData<Playlist<PlaylistItem>>(
                    queryKeys.getPlaylist(data.learning_set_id, {
                        author_id: userId,
                    }),
                    () => data
                );
            },
            onError: () => {
                alert("there was an error");
            },
        }
    );
};
